/**
 * Boolean feature-flag hook backed by PostHog feature flags.
 *
 * Returns `true` only when the flag is explicitly enabled for the current
 * user. Returns `false` while flags are still loading, when the flag is
 * disabled, when the flag doesn't exist, or before the user is identified.
 *
 * Usage:
 *
 *   const roleAdaptedHome = useFeatureFlag("home_role_adapted_v1");
 *   if (roleAdaptedHome) { ... }
 *
 * Rollout protocol (per task 0.18 in `docs/moon-launch-plan/EXECUTION_PLAN.md`):
 *   5% → 25% → 100%, watching Sentry + PostHog metrics at each step.
 *
 * Cleanup: once a flag has been at 100% for ~2 weeks with no regressions,
 * remove the flag and the dead code path it gated. Track flag age in
 * `.context/feature-flags.md`; review monthly.
 */
import { useEffect, useState } from "react";
import { usePostHog } from "posthog-react-native";

export function useFeatureFlag(key: string): boolean {
  const posthog = usePostHog();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!posthog) return;

    const update = () => {
      setEnabled(posthog.isFeatureEnabled(key) === true);
    };

    update();
    const unsubscribe = posthog.onFeatureFlags(update);
    return () => {
      unsubscribe();
    };
  }, [posthog, key]);

  return enabled;
}
